/* eslint-disable max-lines */
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Modal,
  Row,
} from 'reactstrap';
import ReactDatetime from 'react-datetime';
import moment from 'moment';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import PayrollService from '../../../../services/Payroll';
import { toast } from 'react-toastify';
import { getFormValues } from '../../../../helpers/forms';
import AsyncSelect from 'react-select/async';
import EmployeeService from '../../../../services/Employee';
import { createOptionsPage } from '../../../../entities/OptionsPaged';
import { createPagedMeta } from '../../../../entities/PagedMeta';

interface DeductionModalProps {
  isOpen: boolean;
  toggle: () => void;
  employees: any[];
  onCreate?: (result: { success: boolean; data?: any; error?: any }) => void;
}

const DeductionModal = ({ isOpen, toggle, employees: _employees, onCreate }: DeductionModalProps) => {
  const { token, currentSchool } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [deductionType, setDeductionType] = useState<'Loan' | 'ChildCare' | 'Other' | ''>('');
  const [deductionPaymentsStartDate, setDeductionPaymentsStartDate] = useState(moment().format());
  const [deductionPaymentsEndDate, setDeductionPaymentsEndDate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const asyncSelectStyles = {
    control: (styles: any) => ({
      ...styles,
      ...{ border: 0 },
    }),
    input: (styles: any) => ({ ...styles, ...{ border: 0 } }),
    placeholder: (styles: any) => ({
      ...styles,
      ...{ fontSize: '0.810rem', fontWeight: 400, lineHeight: 1.5, color: '#8898aa', textOverflow: 'clip' },
    }),
    singleValue: (styles: any) => ({
      ...styles,
      ...{ fontSize: '0.810rem', fontWeight: 400, lineHeight: 1.5, color: '#8898aa', textOverflow: 'clip' },
    }),
  };

  const loadEmployeesToSelect = async (search?: string) => {
    try {
      const newFilteredOptions = {
        filters: [] as any[],
        options: createOptionsPage(createPagedMeta({ perPage: 99999999 })),
      };

      const schoolFilter = {
        field: 'schoolId',
        operation: 'hash',
        value: currentSchool.value,
      };

      newFilteredOptions.filters.push(schoolFilter);

      if (search) {
        newFilteredOptions.filters.push({
          operation: 'or',
          filters: [
            {
              field: 'user.firstName',
              operation: 'ilike',
              value: `%${search}%`,
            },
            {
              field: 'user.lastName',
              operation: 'ilike',
              value: `%${search}%`,
            },
          ],
        });
      }

      newFilteredOptions.filters.push({
        field: 'status',
        operation: '=',
        value: 'ACTIVE',
      });

      const result = await EmployeeService(token || '').list(newFilteredOptions);
      const dataResult = result?.data || [];

      return dataResult.map((d: any) => ({
        label: `${d.user.firstName} ${d.user.lastName}`,
        value: d.userId,
        photo: d.user.picture,
      }));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error loading employees');
      return [];
    }
  };

  const formatOptionLabel = (option: any) => {
    const imgStyle = {
      borderRadius: '50%',
      verticalAlign: 'middle',
      marginRight: 10,
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img alt="" style={imgStyle} width="28" height="28" src={option.photo} />
        <span>{option.label}</span>
      </div>
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectFile = event.target.files?.[0];
    if (selectFile) {
      setSelectedFile(selectFile);
    }
  };

  const handleCustomButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setDeductionType('');
      setDeductionPaymentsStartDate(moment().format());
      setDeductionPaymentsEndDate('');
      setSelectedEmployee(null);
    }
  }, [isOpen]);

  const computeEndDate = (formBody: any, startDateValue: string) => {
    const amount = parseFloat(String(formBody.amount || '').replace('$', ''));
    const perPayment = parseFloat(String(formBody.amountDeducted || '').replace('$', ''));
    const frequency = formBody.frequency as string;
    if (!startDateValue || !amount || !perPayment || !frequency) return '';
    const period = Math.ceil(amount / perPayment);
    if (frequency === 'per Week') {
      return moment(startDateValue).add(period, 'weeks').format('MM/DD/yyyy');
    }
    if (frequency === 'per Month') {
      return moment(startDateValue).add(period, 'months').format('MM/DD/yyyy');
    }
    return '';
  };

  const recomputeEndDate = (startDateValue?: string) => {
    if (!formRef.current) return;
    const formBody = getFormValues(formRef.current);
    const newEndDate = computeEndDate(formBody, startDateValue || deductionPaymentsStartDate);
    setDeductionPaymentsEndDate(newEndDate);
  };

  const handleSubmitDeduction = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading) return;

    const formBody = getFormValues(event.target);
    const formType = formBody.type as string;

    if (formType === 'Loan' && !selectedFile) {
      toast.error('File is required');
      return;
    }

    const endDate = computeEndDate(formBody, deductionPaymentsStartDate);
    if (formType === 'Loan' && !endDate) {
      toast.error('Please fill start date, amount, amount deducted and frequency');
      return;
    }

    try {
      setLoading(true);
      let result;

      if (formType === 'Loan') {
        const formData = new FormData();
        formData.append('file', selectedFile as any);
        formData.append('userId', formBody.userId);
        formData.append('schoolId', currentSchool?.value);
        formData.append('type', formType);
        formData.append('amount', String(parseFloat(String(formBody.amount).replace('$', ''))));
        formData.append('reason', formBody.reason);
        formData.append('startDate', deductionPaymentsStartDate);
        formData.append('frequency', formBody.frequency);
        formData.append('amountDeducted', String(parseFloat(String(formBody.amountDeducted).replace('$', ''))));

        setDeductionPaymentsEndDate(endDate);
        result = await PayrollService(token || '').deductionCreateWithFile(formData);
      } else {
        const data: any = {
          userId: formBody.userId,
          schoolId: currentSchool?.value,
          type: formType,
          amount: parseFloat(String(formBody.amount).replace('$', '')),
          reason: formBody.reason,
          startDate: deductionPaymentsStartDate,
          frequency: formBody.frequency,
        };
        result = await PayrollService(token || '').deductionCreate(data);
      }

      toast.success(`Saved successfully!`);
      onCreate?.({ success: true, data: result?.data });
    } catch (e: any) {
      const message = String(e?.response?.data?.message || 'Error to create');
      toast.error(message);
      onCreate?.({ success: false, error: e });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className="modal-dialog-centered" isOpen={isOpen} toggle={toggle} size={'xl'}>
      <div className="modal-header">
        <h4 className="modal-title">Record a New Deduction Request</h4>
        <div onClick={toggle} className="d-flex p-1 cursor-pointer">
          <i className="fas fa-times" />
        </div>
      </div>
      <div className="modal-body">
        <div className="px-2">
          <Form innerRef={formRef} onSubmit={handleSubmitDeduction}>
            <Row>
              <Col lg="6">
                <FormGroup>
                  <label className="form-control-label">Name</label>
                  <AsyncSelect
                    cacheOptions
                    defaultOptions
                    loadOptions={loadEmployeesToSelect}
                    styles={asyncSelectStyles}
                    placeholder="Select employee..."
                    value={selectedEmployee}
                    onChange={(employee: any) => setSelectedEmployee(employee)}
                    formatOptionLabel={formatOptionLabel}
                  />
                  <Input name="userId" type="hidden" value={selectedEmployee?.value || ''} required />
                </FormGroup>
              </Col>
              <Col lg="6">
                <FormGroup>
                  <label className="form-control-label">Type</label>
                  <Input
                    name="type"
                    type="select"
                    required
                    onChange={(e) => {
                      setDeductionType(e.target.value as 'Loan' | 'ChildCare' | 'Other' | '');
                    }}
                  >
                    <option value=""></option>
                    <option value="Loan">Loan</option>
                    <option value="ChildCare">ChildCare</option>
                    <option value="Other">Other</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col lg="6">
                <label className="form-control-label">$ Amount</label>
                <Input name="amount" type="text" required />
              </Col>

              <Col lg="6">
                <label className="form-control-label">Reason</label>
                <Input name="reason" type="text" required />
              </Col>
            </Row>

            {deductionType === 'Loan' && (
              <>
                <hr />
                <Row className="mt-4">
                  <Col lg="6">
                    <label className="form-control-label">Amount deducted</label>
                    <Input name="amountDeducted" type="text" required />
                  </Col>

                  <Col lg="6">
                    <label className="form-control-label">Frequency</label>
                    <Input name="frequency" type="select" required onChange={() => recomputeEndDate()}>
                      <option value=""></option>
                      <option value="per Week">per Week</option>
                      <option value="per Month">per Month</option>
                    </Input>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">
                        LoanPayments
                        <br /> Start Date
                      </label>
                      <InputGroup className="input-group-alternative">
                        <ReactDatetime
                          inputProps={{
                            name: 'startDate',
                            placeholder: 'Start Date',
                          }}
                          onChange={(newValue) => {
                            const value = `${newValue}`;
                            setDeductionPaymentsStartDate(value);
                            recomputeEndDate(value);
                          }}
                          timeFormat={false}
                          value={`${moment(deductionPaymentsStartDate).format('MM/DD/yyyy')}`}
                          dateFormat={'MM/DD/yyyy'}
                          closeOnSelect={true}
                        />
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-calendar-grid-58" />
                          </InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <label className="form-control-label">
                      LoanPayments
                      <br /> End Date
                    </label>
                    <Input type="text" value={deductionPaymentsEndDate} disabled />
                  </Col>
                </Row>

                <div>
                  <h3>Attach File Here</h3>
                  <div className="d-flex align-items-center">
                    <Button color="primary" onClick={handleCustomButtonClick} style={{ width: 160 }}>
                      Select File
                    </Button>
                    <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} />
                    {selectedFile && (
                      <>
                        <p className="p-0 m-0">Selected File: {selectedFile.name} </p>
                        <div onClick={() => setSelectedFile(null)} className="d-flex p-1 cursor-pointer ml-2">
                          <i className="fas fa-times" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            {(deductionType === 'ChildCare' || deductionType === 'Other') && (
              <>
                <hr />
                <Row className="mt-4">
                  <Col lg="6">
                    <label className="form-control-label">Frequency</label>
                    <Input name="frequency" type="select" required onChange={() => recomputeEndDate()}>
                      <option value=""></option>
                      <option value="per Week">per Week</option>
                      <option value="per Month">per Month</option>
                    </Input>
                  </Col>

                  <Col lg="6">
                    <FormGroup>
                      <label className="form-control-label">Start Date</label>
                      <InputGroup className="input-group-alternative">
                        <ReactDatetime
                          inputProps={{
                            placeholder: 'Start Date',
                          }}
                          onChange={(newValue) => {
                            setDeductionPaymentsStartDate(`${newValue}`);
                          }}
                          timeFormat={false}
                          value={`${moment(deductionPaymentsStartDate).format('MM/DD/yyyy')}`}
                          dateFormat={'MM/DD/yyyy'}
                          closeOnSelect={true}
                        />
                        <InputGroupAddon addonType="prepend">
                          <InputGroupText>
                            <i className="ni ni-calendar-grid-58" />
                          </InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}

            <div className="d-flex justify-content-around mt-4">
              <Button className="btn-secondary" onClick={toggle}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                style={{ background: '#02c412', color: '#FFFFFF', width: '150px' }}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default DeductionModal;
